import { useArticles } from "@faire/help-center-utils/hooks/article/useArticles";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Insider } from "@faire/web--source/slate/icons/InsiderIcon";
import React from "react";

import { OTHER_NODES_LABEL } from "../../shared/other-node";
import { SelfServeNodes } from "../../types";
import { BenefitsIA } from "../instantAnswers/insiderMembership-benefits";
import { BrandDoesNotHaveInsiderFreeShippingIA } from "../instantAnswers/insiderMembership-brandDoesNotHaveInsiderFreeShipping";
import { CancelMembershipIA } from "../instantAnswers/insiderMembership-cancelMembership";
import { DidNotGetInsiderFreeDutiesIA } from "../instantAnswers/insiderMembership-didNotGetInsiderFreeDuties";
import { DidNotGetInsiderFreeShippingIA } from "../instantAnswers/insiderMembership-didNotGetInsiderFreeShipping";
import { HowToFindBrandsThatHaveInsiderFreeShippingIA } from "../instantAnswers/insiderMembership-howToFindBrandsThatHaveInsiderFreeShipping";
import { PauseMembershipIA } from "../instantAnswers/insiderMembership-pauseMembership";
import { ManageInsiderMembershipVisibilityFn } from "../visibilityFunctions/manageInsiderMembershipVisibilityFn";

export const useInsiderMembershipRetailerNodes = (): SelfServeNodes => {
  const { strictLocalize } = useStrictLocalization();
  const articles = useArticles();
  return {
    InsiderMembership: {
      id: "InsiderMembership",
      label: strictLocalize({
        defaultMessage: "Insider membership",
        description: {
          text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      icon: <Insider color="teal" />,
      children: [
        "Benefits",
        "PauseMembership",
        "CancelMembership",
        "FreeDuties",
        "FreeShipping",
        "OtherInsiderMembership",
      ],
      level: 1,
      sidebarTitle: strictLocalize({
        defaultMessage: "Insider membership issue",
        description: {
          text: "Title for displaying the issue on the order summary sidebar",
        },
      }),
    },
    Benefits: {
      id: "Benefits",
      label: strictLocalize({
        defaultMessage: "Benefits",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.FAQ.FAIRE_INSIDER_US, articles.FAQ.FAIRE_INSIDER_EU],
      instantAnswer: BenefitsIA(strictLocalize),
      level: 2,
    },
    PauseMembership: {
      id: "PauseMembership",
      label: strictLocalize({
        defaultMessage: "Pause membership",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.FAQ.FAIRE_INSIDER_US, articles.FAQ.FAIRE_INSIDER_EU],
      isNodeVisible: ManageInsiderMembershipVisibilityFn,
      instantAnswer: PauseMembershipIA(strictLocalize),
      level: 2,
    },
    CancelMembership: {
      id: "CancelMembership",
      label: strictLocalize({
        defaultMessage: "Cancel membership",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.FAQ.FAIRE_INSIDER_US, articles.FAQ.FAIRE_INSIDER_EU],
      isNodeVisible: ManageInsiderMembershipVisibilityFn,
      instantAnswer: CancelMembershipIA(strictLocalize),
      level: 2,
    },
    FreeDuties: {
      id: "FreeDuties",
      label: strictLocalize({
        defaultMessage: "Free duties",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: ["DidNotGetInsiderFreeDuties"],
      level: 2,
    },
    FreeShipping: {
      id: "FreeShipping",
      label: strictLocalize({
        defaultMessage: "Free shipping",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "DidNotGetInsiderFreeShipping",
        "BrandDoesNotHaveInsiderFreeShipping",
        "HowToFindBrandsThatHaveInsiderFreeShipping",
      ],
      level: 2,
    },
    OtherInsiderMembership: {
      id: "OtherInsiderMembership",
      label: OTHER_NODES_LABEL(strictLocalize),
      articles: [articles.FAQ.FAIRE_INSIDER_US, articles.FAQ.FAIRE_INSIDER_EU],
      level: 2,
    },
    DidNotGetInsiderFreeDuties: {
      id: "DidNotGetInsiderFreeDuties",
      label: strictLocalize({
        defaultMessage: "I didn’t get Insider free duties on an order",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.FAQ.FAIRE_INSIDER_US, articles.FAQ.FAIRE_INSIDER_EU],
      instantAnswer: DidNotGetInsiderFreeDutiesIA(strictLocalize),
      level: 3,
    },
    DidNotGetInsiderFreeShipping: {
      id: "DidNotGetInsiderFreeShipping",
      label: strictLocalize({
        defaultMessage: "I didn’t get Insider free shipping on an order",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.FAQ.FAIRE_INSIDER_US, articles.FAQ.FAIRE_INSIDER_EU],
      instantAnswer: DidNotGetInsiderFreeShippingIA(strictLocalize),
      level: 3,
    },
    BrandDoesNotHaveInsiderFreeShipping: {
      id: "BrandDoesNotHaveInsiderFreeShipping",
      label: strictLocalize({
        defaultMessage: "A Brand doesn’t have Insider free shipping anymore",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: BrandDoesNotHaveInsiderFreeShippingIA(strictLocalize),
      level: 3,
    },
    HowToFindBrandsThatHaveInsiderFreeShipping: {
      id: "HowToFindBrandsThatHaveInsiderFreeShipping",
      label: strictLocalize({
        defaultMessage:
          "How can I find brands that have Insider free shipping?",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.FAQ.FAIRE_INSIDER_US, articles.FAQ.FAIRE_INSIDER_EU],
      instantAnswer:
        HowToFindBrandsThatHaveInsiderFreeShippingIA(strictLocalize),
      level: 3,
    },
  };
};
